import { IconX, Logo } from '@developerz/ui';
import { A, useLocation } from '@solidjs/router';
import { createEffect, type JSX, on, Show } from 'solid-js';
import { t } from '../i18n';
import { useDialogTrap } from '../lib/dialog-trap';
import { useScrollLock } from '../lib/overlay';
import {
  ADMIN_NAV,
  ADMIN_RAIL,
  currentRailItem,
  filterRailItemsByScope,
  USER_MENU,
} from '../lib/rail';
import { useMe } from '../lib/session';
import { createRailViewport } from '../lib/viewport';
import { LanguagePicker } from './LanguagePicker';
import { OrgRail } from './OrgRail';
import { OrgSwitcher } from './OrgSwitcher';
import { RailGroups } from './RailGroups';
import { RailTip } from './RailTip';
import { RailToggle } from './RailToggle';
import { SidebarCtas } from './SidebarCtas';
import { UserMenu } from './UserMenu';
import { buttonClass } from './ui/Button';

// Sidebar (the canvas rail, 08 / APP): brand → `org / <login>` switcher → the
// job-grouped rail → setup + install CTAs → the phone's language picker → user
// block, the trigger of the account menu (`UserMenu`) → the collapse toggle. One
// element plays fixed rail AND mobile drawer (see _sidebar.scss). The rail's rows
// and groups are `lib/rail.ts`; the org rail's reads live in `OrgRail`.
//
// At `lg` the rail has TWO widths (#7710): the 13.5rem rail, and the 3.5rem icon
// rail it collapses to (`lib/rail-collapse.ts`, `[` or the foot `<RailToggle/>`),
// where every control shows its icon and its label in `<RailTip/>`. The words are
// visually hidden there, never removed, so every control keeps its name. Below
// `lg` the drawer is unchanged: `collapsed` is always false there.
//
// The caller's OWN scope list (`/v1/me`) decides what the two navigation
// surfaces offer, through the ONE gate in `lib/rail.ts`: the rail row and the
// account-menu row are both dropped for a credential that does not hold the
// scope behind them. An absent list keeps every row — the server is the
// authority, and this only picks what is offered.
//
// Open as the drawer it is a modal surface over a scrim — the fifth member of
// `testing/overlay-shells.ts` — so while `open` it announces as a named modal
// dialog and takes the one focus trap (`lib/dialog-trap.ts`: focus in, Tab
// held, Escape closes, focus back to the menu button) and the scroll lock.
// Closed, it is the plain `complementary` rail again: a `<div>` carrying that
// role rather than an `<aside>`, because axe refuses `role="dialog"` on an
// `<aside>` (`aria-allowed-role`). `open` is only ever SET below `lg`: the one
// control that sets it, `.app-topbar__menu`, is hidden at `lg` and up
// (`_shell.scss`), and so is the drawer's own close button. A window widened to
// `lg` with the drawer open therefore closes it on that edge
// (`createRailViewport`), rather than leaving the trap holding Tab in a rail
// with no visible way out.

/** The drawer's id, for the menu button's `aria-controls`. */
export const NAV_DRAWER_ID = 'app-nav-drawer';

type AppSidebarProps = {
  readonly adminHost: boolean;
  readonly resolve: (href: string) => string;
  /** The mobile drawer is open. */
  readonly open: boolean;
  readonly onClose: () => void;
  /** The `lg` rail is the collapsed icon rail (#7710). Always false below `lg`. */
  readonly collapsed: boolean;
  /** The foot toggle's press: `lib/rail-collapse.ts`'s `toggle`. */
  readonly onToggleRail: () => void;
  /** Bearer for `/v1/me`; the test seam (`OrgRail`'s shape). Defaults to `SESSION_TOKEN`. */
  readonly token?: string | null;
};

export function AppSidebar(props: AppSidebarProps): JSX.Element {
  const me = useMe(props.token);
  const location = useLocation();
  let drawer: HTMLDivElement | undefined;
  useDialogTrap(
    () => props.open,
    () => drawer,
    () => props.onClose(),
  );
  useScrollLock(() => props.open);
  // The widening EDGE only (`defer`): a drawer is never opened at `lg`.
  const rail = createRailViewport();
  createEffect(
    on(
      rail,
      (atLg) => {
        if (atLg && props.open) props.onClose();
      },
      { defer: true },
    ),
  );
  return (
    // biome-ignore lint/a11y/useAriaPropsSupportedByRole: the role is dynamic, `dialog` (which takes aria-modal) exactly while aria-modal is set; biome cannot read a role expression.
    <div
      id={NAV_DRAWER_ID}
      class="app-sidebar"
      ref={drawer}
      role={props.open ? 'dialog' : 'complementary'}
      aria-modal={props.open ? 'true' : undefined}
      aria-label={props.open ? t('nav.primary') : undefined}
    >
      <div class="app-sidebar__head">
        <A class="app-brand" href={props.resolve('/')} end data-rail-tip={t('app.title')}>
          <span class="app-brand__mark" aria-hidden="true">
            {/* The cube mark: inline `currentColor` SVG coloured by `.dz-logo`
                (`--foreground`, `_base.scss`), so it inverts with the theme.
                Decorative here — the name beside it is the link's text. */}
            <Logo variant="icon" height={18} />
          </span>
          <span class="app-brand__name">{t('app.title')}</span>
        </A>
        <button
          type="button"
          class={`${buttonClass({ variant: 'ghost', size: 'icon-sm' })} app-sidebar__close`}
          aria-label={t('nav.close_menu')}
          onClick={props.onClose}
        >
          <IconX size="1rem" />
        </button>
      </div>

      {/* Account context: the tenant you're acting as. Hidden in the collapsed
          icon rail (expand to switch org), where the topbar's `/org/<org>` path
          (`.app-topbar__path`, from `md` up) is what stays visible. Switching
          re-scopes every account-keyed query (lib/active-account). */}
      <Show when={!props.adminHost}>
        <OrgSwitcher />
      </Show>

      <Show when={!props.adminHost}>
        <OrgRail resolve={props.resolve} scopes={me.data?.scopes} collapsed={props.collapsed} />
      </Show>
      <Show when={props.adminHost && me.data?.is_platform_admin}>
        <RailGroups
          label={t('nav.admin')}
          groups={ADMIN_RAIL}
          resolve={(href) => href}
          current={currentRailItem(location.pathname, ADMIN_NAV, (href) => href)?.href}
          collapsed={props.collapsed}
        />
      </Show>

      <Show when={!props.adminHost}>
        <SidebarCtas resolve={props.resolve} />
      </Show>

      {/* The phone's language picker (#6222): the top bar hides its own below
          `sm`, and this one shows ONLY there (`_sidebar.scss`). */}
      <LanguagePicker id="drawer-locale" class="app-sidebar__locale" block />

      {/* The admin host's URLs carry no org: an `/account/*` row would resolve
          bare and `LegacyRedirect` would bounce it to `/admin`, so there the
          menu is Sign out alone. */}
      <Show when={me.data}>
        {(data) => (
          <UserMenu
            login={data().github_login}
            sub={data().email ?? data().role ?? ''}
            avatar={data().user?.avatar ?? null}
            links={props.adminHost ? [] : filterRailItemsByScope(USER_MENU, data().scopes)}
            resolve={props.resolve}
          />
        )}
      </Show>

      {/* The one tooltip (fixed, so its DOM spot is free), then the toggle: the rail's
          last child. Not mounted in the open drawer: it is `display: none` there, and
          the trap's last stop must be one Tab can reach, or Tab leaves the dialog. */}
      <RailTip root={() => drawer} enabled={props.collapsed} />
      <Show when={!props.open}>
        <RailToggle
          collapsed={props.collapsed}
          onToggle={props.onToggleRail}
          controls={NAV_DRAWER_ID}
        />
      </Show>
    </div>
  );
}
